import React from "react";
import type { DisplayPlayerProp } from "./utils-v2";
import type { BookOddsData } from "./comparison-table-types";
import {
  canOpenBookHistory,
  findBookSeries,
  useEventLineHistory,
} from "@/components/odds/line-sparkline";
import {
  canAnchorOddsHistory,
  teamsForSide,
  type OpenOddsHistory,
  type OddsHistorySelection,
  type OddsHistorySelectionContext,
} from "@/components/odds/odds-history-selection";
import { toBetLinkSet } from "@/lib/bet-links";
import { OddsHistoryTrigger } from "@/components/odds/odds-history-trigger";
import { buildOverlayPeersFromQuotes } from "@/components/odds/overlay-peers";
import { MovementDelta } from "@/components/odds/movement-delta";
import { getBookDisplayName } from "@/lib/utils/sports-books";
import { bestAtOwnLine, rowHistoryOpposite } from "./history-opposite";

export function OptimizerExpandedHistory({
  row,
  books,
  openHistory,
  children,
}: {
  row: DisplayPlayerProp;
  books: BookOddsData[];
  openHistory: OpenOddsHistory;
  children: (helpers: {
    renderHistoryTrigger: (book: BookOddsData) => React.ReactNode;
    renderMovementDelta: (book: BookOddsData) => React.ReactNode;
  }) => React.ReactNode;
}) {
  const eventId = typeof row.event_id === "string" ? row.event_id : "";
  const primaryBook = row.primary_book ?? "";
  const primarySelectionId =
    typeof row.selection_id === "string" ? row.selection_id : "";
  const side = (row.selection_side ?? row.bet_type ?? "OVER") as string;
  const sideLc = side.toLowerCase();
  const eventStart = row.game_start_timestamp
    ? new Date(row.game_start_timestamp)
    : null;

  const selectionKeys = React.useMemo(() => {
    const ids = new Set<string>();
    if (primarySelectionId) {
      ids.add(primarySelectionId);
    }
    for (const book of books) {
      if (book.selectionId) {
        ids.add(book.selectionId);
      }
    }
    return [...ids].map((id) => `${id}|${sideLc}`);
  }, [books, primarySelectionId, sideLc]);

  const lineHistory = useEventLineHistory(
    eventId,
    Boolean(eventId) && selectionKeys.length > 0,
    eventStart,
    selectionKeys,
  );

  const rowContext = React.useMemo<OddsHistorySelectionContext>(
    () => ({
      fair:
        row.consensus_implied_probability != null &&
        row.consensus_implied_probability > 0 &&
        row.consensus_implied_probability < 1
          ? {
              display: "probability",
              probability: row.consensus_implied_probability,
            }
          : undefined,
      value:
        row.ev_percentage != null && Number.isFinite(row.ev_percentage)
          ? { kind: "probability_edge", percent: row.ev_percentage }
          : undefined,
    }),
    [row.consensus_implied_probability, row.ev_percentage],
  );

  const linksFor = (bookId: string, resolved: string | null | undefined) =>
    row.__comparison_links?.[bookId] ??
    toBetLinkSet(resolved, undefined, undefined) ??
    undefined;

  const altBest = React.useMemo(() => bestAtOwnLine(books, (book) => book.selectionOdds), [books]);
  const historyPeers = React.useMemo(
    () => {
      const primaryComparison = books.find((book) => book.book === primaryBook);
      return buildOverlayPeersFromQuotes([
        {
          book: primaryBook,
          selectionId: primarySelectionId,
          line: row.line ?? null,
          oddsAmerican: row.primary_odds,
          betLinks: linksFor(primaryBook, row.bet_link),
          isBestPrice:
            primaryComparison?.isBestSelection === true
              ? (true as const)
              : undefined,
        },
        ...books.map((book) => ({
          book: book.book,
          selectionId: book.selectionId,
          line: book.line ?? row.line ?? null,
          oddsAmerican: book.selectionOdds,
          betLinks: linksFor(book.book, book.betLink),
          isBestPrice: book.isBestSelection === true || altBest(book) ? (true as const) : undefined,
        })),
      ]);
    },
    [books, altBest, primaryBook, primarySelectionId, row.line, row.primary_odds, row.bet_link, row.__comparison_links],
  );

  const buildSelection = (
    book: string,
    selectionId: string | null | undefined,
    line: number | null | undefined,
  ): OddsHistorySelection => ({
    eventId,
    book,
    selectionId: selectionId ?? undefined,
    side,
    line: line ?? null,
    market: row.stat_type,
    marketLabel: row.market_display ?? row.stat_type,
    entity: row.player_name,
    eventStart: row.game_start_timestamp ?? null,
    matchup: row.event_name ?? row.player_name,
    teams: teamsForSide(side, row.team, row.opponent, row.team_short, row.opponent_short),
  });

  const seriesFor = (book: string, selectionId: string | null | undefined) =>
    selectionId
      ? findBookSeries(lineHistory, { book, selectionId, side })
      : null;

  const renderHistoryTrigger = (book: BookOddsData): React.ReactNode => {
    const selection: OddsHistorySelection = {
      ...buildSelection(book.book, book.selectionId, book.line ?? row.line),
      opposite: rowHistoryOpposite(row, books, book.book),
      context: {
        ...(book.line === undefined ? rowContext : {}),
        quote: {
          oddsAmerican:
            typeof book.selectionOdds === "number" &&
            Number.isFinite(book.selectionOdds)
              ? book.selectionOdds
              : undefined,
          betLinks: linksFor(book.book, book.betLink),
          isBestPrice: book.isBestSelection === true || altBest(book) ? true : undefined,
        },
      },
    };
    const series = seriesFor(book.book, book.selectionId);
    if (
      !canAnchorOddsHistory(selection) ||
      !canOpenBookHistory(series, {
        book: book.book,
        selectionId: book.selectionId,
        side,
      })
    ) {
      return null;
    }
    return (
      <OddsHistoryTrigger
        mode="reveal"
        bookLabel={getBookDisplayName(book.book)}
        onOpen={() =>
          openHistory({
            selection,
            fallbackSeries: series,
            peers: historyPeers,
            initialView: "book",
          })
        }
      />
    );
  };

  const renderMovementDelta = (book: BookOddsData): React.ReactNode => (
    <MovementDelta series={seriesFor(book.book, book.selectionId)} />
  );

  return <>{children({ renderHistoryTrigger, renderMovementDelta })}</>;
}
